import React, { useState, useEffect, useMemo } from 'react';
import {
  FaWhatsapp,
  FaChevronDown,
  FaCalendarAlt,
  FaInfoCircle,
  FaExclamationTriangle
} from 'react-icons/fa';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import '../../styles/whatsapp/TemplateInsights.css';
import { authFetch } from '../../utils/authFetch';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://krmmadhubanibackend.onrender.com';

// Cost per delivered message in INR (configurable)
const COST_PER_DELIVERED = 0.12;

const todayUnix = () => Math.floor(Date.now() / 1000);
const daysAgoUnix = (days) => Math.floor((Date.now() - days * 24 * 60 * 60 * 1000) / 1000);

const formatDateLabel = (unix) => {
  const d = new Date(unix * 1000);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const formatDateRange = (startUnix, endUnix) => {
  const opts = { day: 'numeric', month: 'short', year: 'numeric' };
  const s = new Date(startUnix * 1000).toLocaleDateString('en-IN', opts);
  const e = new Date(endUnix * 1000).toLocaleDateString('en-IN', opts);
  return `${s} - ${e}`;
};

const toInputDate = (unix) => new Date(unix * 1000).toISOString().slice(0, 10);
const fromInputDate = (str) => Math.floor(new Date(str).getTime() / 1000);

const formatCurrency = (n) => `₹${n.toFixed(2)}`;

const TemplateInsights = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('trend');
  const [errorTab, setErrorTab] = useState('top');
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Date range (default: last 21 days)
  const [startDate, setStartDate] = useState(daysAgoUnix(21));
  const [endDate, setEndDate] = useState(todayUnix());

  // Selected metrics for chart
  const [selectedMetrics, setSelectedMetrics] = useState({
    sent: true,
    delivered: true,
    read: true,
    replies: true
  });

  // Fetch templates list
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoadingTemplates(true);
        setError(null);
        const res = await authFetch(`${API_BASE_URL}/api/whatsapp/templates`);
        const data = await res.json();
        if (data.success) {
          const approved = (data.templates || []).filter(t => t.status === 'APPROVED' || t.status === 'PENDING' || t.status === 'ACTIVE');
          setTemplates(approved);
          if (approved.length > 0) {
            setSelectedTemplate(approved[0]);
          }
        } else {
          setError(data.error || 'Failed to load templates');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, []);

  // Fetch analytics whenever template or date range changes
  useEffect(() => {
    if (!selectedTemplate) return;

    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);
        const url = `${API_BASE_URL}/api/whatsapp/template-analytics?start=${startDate}&end=${endDate}&template_ids=${selectedTemplate.id}&granularity=DAILY`;
        const res = await authFetch(url);
        const data = await res.json();
        if (data.success) {
          setAnalytics(data.analytics);
        } else {
          setError(data.error || 'Failed to load analytics');
          setAnalytics(null);
        }
      } catch (err) {
        setError(err.message);
        setAnalytics(null);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [selectedTemplate, startDate, endDate]);

  // Process analytics into chart data and totals
  const { chartData, totals } = useMemo(() => {
    const empty = {
      chartData: [],
      totals: { sent: 0, delivered: 0, read: 0, replies: 0 }
    };
    if (!analytics?.data || analytics.data.length === 0) return empty;

    // Meta returns data nested under data[0].data_points
    const dataPoints = analytics.data[0]?.data_points || [];

    // Sum click counts. Meta's `clicked` field is an array of objects:
    // [{ type, button_content, count }, ...] — not a number.
    const sumClicks = (clicked) => {
      if (typeof clicked === 'number') return clicked;
      if (Array.isArray(clicked)) {
        return clicked.reduce((sum, c) => sum + (Number(c?.count) || 0), 0);
      }
      return 0;
    };

    // Aggregate by start timestamp
    const byDate = {};
    dataPoints.forEach(point => {
      const dateKey = point.start; // unix
      if (!byDate[dateKey]) {
        byDate[dateKey] = { date: dateKey, sent: 0, delivered: 0, read: 0, replies: 0 };
      }
      byDate[dateKey].sent += Number(point.sent) || 0;
      byDate[dateKey].delivered += Number(point.delivered) || 0;
      byDate[dateKey].read += Number(point.read) || 0;
      byDate[dateKey].replies += sumClicks(point.clicked);
    });

    const sortedDates = Object.keys(byDate).map(Number).sort((a, b) => a - b);
    const chartData = sortedDates.map(d => ({
      label: formatDateLabel(d),
      sent: byDate[d].sent,
      delivered: byDate[d].delivered,
      read: byDate[d].read,
      replies: byDate[d].replies
    }));

    const totals = chartData.reduce(
      (acc, d) => ({
        sent: acc.sent + d.sent,
        delivered: acc.delivered + d.delivered,
        read: acc.read + d.read,
        replies: acc.replies + d.replies
      }),
      { sent: 0, delivered: 0, read: 0, replies: 0 }
    );

    return { chartData, totals };
  }, [analytics]);

  const amountSpent = totals.delivered * COST_PER_DELIVERED;
  const costPerMessage = totals.delivered > 0 ? amountSpent / totals.delivered : 0;
  const readRate = totals.delivered > 0 ? Math.round((totals.read / totals.delivered) * 100) : 0;

  const getTemplateBodyText = (template) => {
    if (!template?.components) return '';
    const body = template.components.find(c => c.type === 'BODY');
    return body?.text || '';
  };

  const getTemplateHeaderText = (template) => {
    if (!template?.components) return '';
    const header = template.components.find(c => c.type === 'HEADER');
    if (!header) return '';
    if (header.format === 'TEXT') return header.text;
    if (header.format === 'IMAGE') return '[Image attachment]';
    if (header.format === 'DOCUMENT') return '[Document attachment]';
    return '';
  };

  const getTemplateFooterText = (template) => {
    if (!template?.components) return '';
    const footer = template.components.find(c => c.type === 'FOOTER');
    return footer?.text || '';
  };

  const handleApplyDate = (start, end) => {
    setStartDate(fromInputDate(start));
    setEndDate(fromInputDate(end));
    setShowDatePicker(false);
  };

  const toggleMetric = (key) => {
    setSelectedMetrics(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectedMetricsCount = Object.values(selectedMetrics).filter(Boolean).length;

  const renderStatusBadge = (status) => {
    const map = {
      APPROVED: { label: 'Active - Quality pending', cls: 'badge-active' },
      PENDING: { label: 'Pending review', cls: 'badge-pending' },
      REJECTED: { label: 'Rejected', cls: 'badge-rejected' },
      DISABLED: { label: 'Disabled', cls: 'badge-disabled' }
    };
    const m = map[status] || { label: status, cls: 'badge-default' };
    return <span className={`status-badge ${m.cls}`}>{m.label}</span>;
  };

  if (loadingTemplates) {
    return (
      <div className="ti-page">
        <div className="ti-loading">
          <div className="ti-spinner"></div>
          <p>Loading templates...</p>
        </div>
      </div>
    );
  }

  if (error && !selectedTemplate) {
    return (
      <div className="ti-page">
        <div className="ti-error-state">
          <FaExclamationTriangle className="ti-error-icon" />
          <h3>Unable to load template insights</h3>
          <p>{error}</p>
          <p className="ti-error-hint">
            Make sure <code>WHATSAPP_BUSINESS_ACCOUNT_ID</code> is set in the backend environment variables
            and your access token has <code>whatsapp_business_management</code> permission.
          </p>
        </div>
      </div>
    );
  }

  if (!selectedTemplate) {
    return (
      <div className="ti-page">
        <div className="ti-empty-state">
          <p>No templates found in your WhatsApp Business Account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ti-page">
      {/* Top header: template selector + date range + actions */}
      <div className="ti-top-bar">
        <div className="ti-template-selector">
          <div className="ti-template-icon">
            <FaWhatsapp />
          </div>
          <div className="ti-template-meta">
            <button
              className="ti-template-name-btn"
              onClick={() => setShowTemplateDropdown(v => !v)}
            >
              <span className="ti-template-name">{selectedTemplate.name}</span>
              <span className="ti-template-lang">· {selectedTemplate.language || 'en'}</span>
              <FaChevronDown className="ti-chevron" />
            </button>
            <div className="ti-template-substatus">
              {renderStatusBadge(selectedTemplate.status)}
              <span className="ti-dot">·</span>
              <span className="ti-template-cat">{selectedTemplate.category || 'Utility'}</span>
            </div>
            {showTemplateDropdown && (
              <div className="ti-dropdown">
                {templates.map(t => (
                  <div
                    key={t.id}
                    className={`ti-dropdown-item ${t.id === selectedTemplate.id ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedTemplate(t);
                      setShowTemplateDropdown(false);
                    }}
                  >
                    <span className="ti-dropdown-item-name">{t.name}</span>
                    <span className="ti-dropdown-item-lang">{t.language}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="ti-top-actions">
          <button
            className="ti-date-btn"
            onClick={() => setShowDatePicker(v => !v)}
          >
            <FaCalendarAlt />
            <span>{formatDateRange(startDate, endDate)}</span>
            <FaChevronDown className="ti-chevron" />
          </button>
          {showDatePicker && (
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onApply={handleApplyDate}
              onClose={() => setShowDatePicker(false)}
            />
          )}
        </div>
      </div>

      {/* Main grid */}
      <div className="ti-grid">
        {/* Left sidebar */}
        <aside className="ti-sidebar">
          {/* Template preview */}
          <div className="ti-card ti-template-preview-card">
            <h4 className="ti-card-title">Your template</h4>
            <div className="ti-template-preview">
              {(() => {
                const headerText = getTemplateHeaderText(selectedTemplate);
                const bodyText = getTemplateBodyText(selectedTemplate);
                const footerText = getTemplateFooterText(selectedTemplate);
                return (
                  <>
                    {headerText && (
                      <div className="ti-preview-header">{headerText}</div>
                    )}
                    {bodyText && (
                      <div className="ti-preview-body">{bodyText}</div>
                    )}
                    {footerText && (
                      <div className="ti-preview-footer">{footerText}</div>
                    )}
                    <div className="ti-preview-time">{new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}</div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Benchmarks */}
          <div className="ti-card ti-benchmark-card">
            <h4 className="ti-card-title">
              Template benchmarks <FaInfoCircle className="ti-info-icon" />
            </h4>
            <p className="ti-benchmark-period">{formatDateRange(startDate, endDate)}</p>
            <p className="ti-benchmark-message">
              Your read rate is similar to others
            </p>
            <div className="ti-benchmark-stats">
              <div className="ti-benchmark-stat">
                <span className="ti-benchmark-label">This template</span>
                <span className="ti-benchmark-value">{readRate}%</span>
              </div>
              <div className="ti-benchmark-stat">
                <span className="ti-benchmark-label">Similar templates</span>
                <span className="ti-benchmark-value muted">82%</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="ti-main">
          {/* Top metric cards */}
          <div className="ti-top-cards">
            <div className="ti-card ti-metric-large">
              <h4 className="ti-card-title">
                Amount spent <FaInfoCircle className="ti-info-icon" />
              </h4>
              <div className="ti-metric-value">{formatCurrency(amountSpent)}</div>
            </div>
            <div className="ti-card ti-metric-large">
              <h4 className="ti-card-title">
                Cost per message delivered <FaInfoCircle className="ti-info-icon" />
              </h4>
              <div className="ti-metric-value">{formatCurrency(costPerMessage)}</div>
            </div>
          </div>

          {/* Performance section */}
          <div className="ti-card ti-performance-card">
            <h3 className="ti-section-title">
              Performance <FaInfoCircle className="ti-info-icon" />
            </h3>
            <div className="ti-tabs">
              <button
                className={`ti-tab ${activeTab === 'trend' ? 'active' : ''}`}
                onClick={() => setActiveTab('trend')}
              >
                Trend
              </button>
              <button
                className={`ti-tab ${activeTab === 'funnel' ? 'active' : ''}`}
                onClick={() => setActiveTab('funnel')}
              >
                Funnel
              </button>
            </div>

            {/* Metric cards */}
            <div className="ti-metric-row">
              <MetricCard
                label="Messages sent"
                value={totals.sent}
                active={selectedMetrics.sent}
                onClick={() => toggleMetric('sent')}
                color="#f87171"
              />
              <MetricCard
                label="Messages delivered"
                value={totals.delivered}
                active={selectedMetrics.delivered}
                onClick={() => toggleMetric('delivered')}
                color="#9ca3af"
              />
              <MetricCard
                label="Messages read"
                value={totals.read}
                pct={totals.delivered > 0 ? `(${readRate}%)` : null}
                active={selectedMetrics.read}
                onClick={() => toggleMetric('read')}
                color="#10b981"
              />
              <MetricCard
                label="Unique replies"
                value={totals.replies}
                active={selectedMetrics.replies}
                onClick={() => toggleMetric('replies')}
                color="#1f2937"
              />
            </div>

            {/* Chart */}
            {activeTab === 'trend' ? (
              <>
                <div className="ti-metrics-selector">
                  <span className="ti-metrics-selector-text">{selectedMetricsCount} metrics selected</span>
                  <FaChevronDown className="ti-chevron" />
                </div>
                {loading ? (
                  <div className="ti-chart-loading">
                    <div className="ti-spinner"></div>
                  </div>
                ) : chartData.length === 0 ? (
                  <div className="ti-chart-empty">
                    <p>No data available for the selected date range</p>
                  </div>
                ) : (
                  <div className="ti-chart-container">
                    <ResponsiveContainer width="100%" height={360}>
                      <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="label" stroke="#6b7280" fontSize={12} />
                        <YAxis stroke="#6b7280" fontSize={12} />
                        <Tooltip />
                        <Legend />
                        {selectedMetrics.sent && (
                          <Line type="monotone" dataKey="sent" name="Messages sent" stroke="#f87171" strokeWidth={2} dot={{ r: 3 }} />
                        )}
                        {selectedMetrics.delivered && (
                          <Line type="monotone" dataKey="delivered" name="Messages delivered" stroke="#9ca3af" strokeWidth={2} dot={{ r: 3 }} />
                        )}
                        {selectedMetrics.read && (
                          <Line type="monotone" dataKey="read" name="Messages read" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                        )}
                        {selectedMetrics.replies && (
                          <Line type="monotone" dataKey="replies" name="Unique replies" stroke="#1f2937" strokeWidth={2} dot={{ r: 3 }} />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            ) : (
              <FunnelView totals={totals} />
            )}
          </div>

          {/* Error messages section */}
          <div className="ti-card ti-errors-card">
            <h3 className="ti-section-title">
              Error messages <FaInfoCircle className="ti-info-icon" />
            </h3>
            <p className="ti-section-subtitle">Select an error for details.</p>
            <div className="ti-tabs ti-tabs-small">
              <button
                className={`ti-tab ${errorTab === 'top' ? 'active' : ''}`}
                onClick={() => setErrorTab('top')}
              >
                Top errors
              </button>
              <button
                className={`ti-tab ${errorTab === 'all' ? 'active' : ''}`}
                onClick={() => setErrorTab('all')}
              >
                All errors
              </button>
            </div>
            <div className="ti-error-list">
              {totals.sent - totals.delivered > 0 ? (
                <div className="ti-error-item">
                  <div className="ti-error-info">
                    <strong>Undeliverable messages</strong>
                    <span className="ti-error-meta">Failed to deliver to recipient</span>
                  </div>
                  <div className="ti-error-count">
                    <strong>{totals.sent - totals.delivered}</strong>
                    <span>messages</span>
                  </div>
                </div>
              ) : (
                <div className="ti-error-empty">
                  <p>No errors during this period 🎉</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// MetricCard component
const MetricCard = ({ label, value, pct, active, onClick, color }) => {
  return (
    <button
      className={`ti-metric-card ${active ? 'active' : 'inactive'}`}
      onClick={onClick}
      style={{ '--card-color': color }}
    >
      <span className="ti-metric-label">
        {label} <FaInfoCircle className="ti-info-icon-small" />
      </span>
      <div className="ti-metric-value-row">
        <span className="ti-metric-value-num">{(Number(value) || 0).toLocaleString('en-IN')}</span>
        {pct && <span className="ti-metric-pct">{pct}</span>}
      </div>
    </button>
  );
};

// Funnel view component
const FunnelView = ({ totals }) => {
  const sent = totals.sent || 1;
  const stages = [
    { label: 'Sent', value: totals.sent, color: '#f87171' },
    { label: 'Delivered', value: totals.delivered, color: '#9ca3af' },
    { label: 'Read', value: totals.read, color: '#10b981' },
    { label: 'Replies', value: totals.replies, color: '#1f2937' }
  ];
  return (
    <div className="ti-funnel">
      {stages.map((s, i) => {
        const pct = Math.round((s.value / sent) * 100);
        return (
          <div key={i} className="ti-funnel-row">
            <div className="ti-funnel-label">{s.label}</div>
            <div className="ti-funnel-bar-wrap">
              <div
                className="ti-funnel-bar"
                style={{ width: `${pct}%`, background: s.color }}
              ></div>
              <span className="ti-funnel-bar-value">{s.value.toLocaleString('en-IN')} ({pct}%)</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Date range picker
const DateRangePicker = ({ startDate, endDate, onApply, onClose }) => {
  const [start, setStart] = useState(toInputDate(startDate));
  const [end, setEnd] = useState(toInputDate(endDate));

  const presets = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 21 days', days: 21 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 }
  ];

  const applyPreset = (days) => {
    const e = todayUnix();
    const s = daysAgoUnix(days);
    setStart(toInputDate(s));
    setEnd(toInputDate(e));
  };

  return (
    <>
      <div className="ti-date-picker-overlay" onClick={onClose}></div>
      <div className="ti-date-picker">
        <div className="ti-date-presets">
          {presets.map(p => (
            <button
              key={p.days}
              className="ti-preset-btn"
              onClick={() => applyPreset(p.days)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="ti-date-inputs">
          <label>
            Start
            <input type="date" value={start} onChange={e => setStart(e.target.value)} />
          </label>
          <label>
            End
            <input type="date" value={end} onChange={e => setEnd(e.target.value)} />
          </label>
        </div>
        <div className="ti-date-actions">
          <button className="ti-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="ti-btn-apply" onClick={() => onApply(start, end)}>Apply</button>
        </div>
      </div>
    </>
  );
};

export default TemplateInsights;
